import { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { Link } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, AreaChart, Area, XAxis, YAxis, CartesianGrid } from 'recharts';
import { format } from 'date-fns';
import { ClickToCall } from '@/components/ClickToCall';

function CRMDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [productAnalytics, setProductAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const canViewApprovals = ['admin', 'manager', 'team_leader'].includes(user?.role);
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const promises = [
        axios.get(`${API}/dashboard/crm-stats`),
        axios.get(`${API}/leads`),
        axios.get(`${API}/meetings/upcoming`).catch(() => ({ data: [] })),
        axios.get(`${API}/products/analytics/summary`).catch(() => ({ data: null })),
      ];
      
      if (canViewApprovals) {
        promises.push(axios.get(`${API}/approvals/pending`).catch(() => ({ data: [] })));
      }
      
      const results = await Promise.all(promises);
      setStats(results[0].data);
      setRecentLeads(results[1].data.slice(0, 10));
      setUpcomingMeetings(results[2].data || []);
      setProductAnalytics(results[3].data);
      if (canViewApprovals && results[4]) {
        setPendingApprovals(results[4].data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprovalAction = async (approvalId, action) => {
    try {
      await axios.post(`${API}/approvals/${approvalId}/action`, {
        action: action,
        notes: action === 'approve' ? 'Approved via dashboard' : 'Rejected via dashboard',
      });
      fetchData();
    } catch (error) {
      console.error('Error processing approval:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-10 h-10 border-4 border-slate-200 dark:border-slate-700 border-t-slate-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  const pipelineData = [
    { name: 'New', value: stats?.new_leads || 0, color: '#334155' },
    { name: 'Contacted', value: stats?.contacted || 0, color: '#475569' },
    { name: 'Qualified', value: stats?.qualified || 0, color: '#64748B' },
    { name: 'Meeting', value: stats?.meetings || 0, color: '#94A3B8' },
    { name: 'Won', value: stats?.closed_won || 0, color: '#22C55E' },
    { name: 'Lost', value: stats?.closed_lost || 0, color: '#EF4444' },
  ];

  const activityTrendData = [
    { name: 'Mon', calls: 12, emails: 18, meetings: 3 },
    { name: 'Tue', calls: 15, emails: 22, meetings: 5 },
    { name: 'Wed', calls: 8, emails: 15, meetings: 2 },
    { name: 'Thu', calls: 20, emails: 28, meetings: 7 },
    { name: 'Fri', calls: 18, emails: 25, meetings: 4 },
  ];

  const getStatusBadge = (status) => {
    const styles = {
      new: 'badge-blue',
      contacted: 'badge-purple',
      qualified: 'badge-green',
      meeting: 'badge-amber',
      closed_won: 'bg-emerald-100 text-emerald-800',
      closed_lost: 'badge-red',
    };
    return `badge ${styles[status] || 'badge-slate'}`;
  };

  return (
    <div className="space-y-6" data-testid="crm-dashboard">
      {/* Welcome Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, {user?.name?.split(' ')[0]}!</h1>
          <p className="mt-1 text-slate-500 dark:text-slate-400 font-body">Here's what's happening with your sales pipeline today.</p>
        </div>
        <Link
          to="/add-lead"
          className="btn-primary"
          data-testid="add-lead-button"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add New Lead
        </Link>
      </div>

      {/* KPI Cards - Colorful */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="kpi-card-blue">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600 mb-1">Total Leads</p>
              <p className="text-3xl font-bold text-blue-900">{stats?.total_leads || 0}</p>
              <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd"/></svg>
                +12% this month
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-200 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="kpi-card-green">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-emerald-600 mb-1">Qualified</p>
              <p className="text-3xl font-bold text-emerald-900">{stats?.qualified || 0}</p>
              <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd"/></svg>
                +8% this week
              </p>
            </div>
            <div className="w-12 h-12 bg-emerald-200 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="kpi-card-amber">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-amber-600 mb-1">Meetings</p>
              <p className="text-3xl font-bold text-amber-900">{stats?.meetings || 0}</p>
              <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd"/></svg>
                +5 scheduled
              </p>
            </div>
            <div className="w-12 h-12 bg-amber-200 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="kpi-card-purple">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-600 mb-1">Closed Won</p>
              <p className="text-3xl font-bold text-purple-900">{stats?.closed_won || 0}</p>
              <p className="text-xs text-purple-600 mt-1 flex items-center gap-1">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd"/></svg>
                +15% conversion
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-200 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 flex items-center gap-4">
          <div className="w-10 h-10 bg-slate-200 dark:bg-slate-600 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-slate-600 dark:text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </div>
          <div>
            <p className="text-xl font-bold text-slate-900 dark:text-white">{stats?.calls || 0}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Calls Made</p>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 flex items-center gap-4">
          <div className="w-10 h-10 bg-slate-200 dark:bg-slate-600 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-slate-600 dark:text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <p className="text-xl font-bold text-slate-900 dark:text-white">{stats?.emails || 0}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Emails Sent</p>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 flex items-center gap-4">
          <div className="w-10 h-10 bg-slate-200 dark:bg-slate-600 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-slate-600 dark:text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </div>
          <div>
            <p className="text-xl font-bold text-slate-900 dark:text-white">{stats?.follow_ups || 0}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Follow-ups</p>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 flex items-center gap-4">
          <div className="w-10 h-10 bg-slate-200 dark:bg-slate-600 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-slate-600 dark:text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <div>
            <p className="text-xl font-bold text-slate-900 dark:text-white">{stats?.total_activities || 0}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Total Activities</p>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pipeline Chart */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-6">Sales Pipeline</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={pipelineData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={3}
                dataKey="value"
              >
                {pipelineData.map((entry) => (
                  <Cell key={`cell-${entry.name}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #E2E8F0', 
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                }}
              />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Activity Trend */}
        <div className="card p-6">
          <h3 className="section-title mb-6">Weekly Activity Trend</h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={activityTrendData}>
              <defs>
                <linearGradient id="colorCalls" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorEmails" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" stroke="#64748B" fontSize={12} />
              <YAxis stroke="#64748B" fontSize={12} />
              <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px' }} />
              <Area type="monotone" dataKey="calls" stroke="#3B82F6" fillOpacity={1} fill="url(#colorCalls)" strokeWidth={2} />
              <Area type="monotone" dataKey="emails" stroke="#8B5CF6" fillOpacity={1} fill="url(#colorEmails)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bottom Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Approvals */}
        {canViewApprovals && (
          <div className="card" data-testid="pending-approvals-widget">
            <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
              <h3 className="section-title">Pending Approvals</h3>
              {pendingApprovals.length > 0 && (
                <span className="badge badge-amber">{pendingApprovals.length} pending</span>
              )}
            </div>
            <div className="p-4">
              {pendingApprovals.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-emerald-100 flex items-center justify-center">
                    <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-slate-500 dark:text-slate-400 font-body">All caught up! No pending approvals.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingApprovals.slice(0, 5).map((approval) => (
                    <div key={approval.id} className="p-4 bg-slate-50 dark:bg-slate-900 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-semibold text-slate-900 dark:text-white">{approval.lead_name || 'Unknown Lead'}</p>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {approval.type === 'status_change' ? 'Status Change Request' : 'Deal Value Update'} • {approval.requested_by_name}
                          </p>
                        </div>
                        <span className="text-xs text-slate-400">
                          {approval.created_at ? format(new Date(approval.created_at), 'MMM d') : '-'}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleApprovalAction(approval.id, 'approve')}
                          className="flex-1 px-3 py-2 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-sm font-medium rounded-lg hover:bg-slate-800 dark:hover:bg-white transition-colors"
                          data-testid={`approve-${approval.id}`}
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleApprovalAction(approval.id, 'reject')}
                          className="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 text-sm font-medium rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                          data-testid={`reject-${approval.id}`}
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Recent Leads */}
        <div className={`card ${canViewApprovals ? '' : 'lg:col-span-2'}`}>
          <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
            <h3 className="section-title">Recent Leads</h3>
            <Link to="/leads" className="text-sm font-medium text-blue-600 hover:text-blue-700">
              View all
            </Link>
          </div>
          <div className="divide-y divide-slate-100">
            {recentLeads.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-slate-500 dark:text-slate-400 font-body">No leads yet. Add your first lead!</p>
              </div>
            ) : (
              recentLeads.slice(0, 6).map((lead) => (
                <div
                  key={lead.id}
                  className="flex items-center justify-between px-6 py-4 hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900 transition-colors"
                >
                  <Link
                    to={`/leads/${lead.id}`}
                    className="flex items-center gap-3 flex-1"
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold text-sm">
                      {lead.name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">{lead.name}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">{lead.organization || lead.email}</p>
                    </div>
                  </Link>
                  <div className="flex items-center gap-3">
                    {lead.phone && lead.phone !== '-' && (
                      <ClickToCall 
                        phone={lead.phone} 
                        leadId={lead.id} 
                        leadName={lead.name}
                        variant="icon"
                        size="sm"
                      />
                    )}
                    <span className={getStatusBadge(lead.status)}>
                      {lead.status?.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Product Analytics (Admin only) */}
      {isAdmin && productAnalytics && (
        <div className="card p-6">
          <h3 className="section-title mb-6">Product Analytics</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <p className="text-sm text-emerald-600 font-medium">Total Revenue</p>
              <p className="text-2xl font-bold text-emerald-900 mt-1">
                ₹{(productAnalytics.total_revenue || 0).toLocaleString('en-IN')}
              </p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-600 font-medium">Products Sold</p>
              <p className="text-2xl font-bold text-blue-900 mt-1">{productAnalytics.total_units || 0}</p>
            </div>
            {productAnalytics.top_products?.slice(0, 2).map((product, idx) => (
              <div key={idx} className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <p className="text-sm text-purple-600 font-medium truncate">{product.name}</p>
                <p className="text-2xl font-bold text-purple-900 mt-1">₹{product.revenue?.toLocaleString('en-IN')}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default CRMDashboard;
